// Content fields shared by the Add Block dialog and the Block dialog: a description, a list of
// exercises, and a list of targets. A block needs at least one of the three.
import { Button, Form, Input, Popover, Select } from "antd";
import { CloseOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useRtpMetricOptions, useRtpMetricStats } from "./rtpHooks";
import { formatStat } from "./CriterionForm";
import { EXERCISE_FIELDS } from "./ExerciseTable";
import type { RtpBlockContent, RtpFieldTarget, RtpGymExercise } from "./types";

const { TextArea } = Input;

export const BLOCK_CONTENT_ERROR = "Add a description, an exercise, or a target.";

export interface BlockFormValues {
	description?: string;
	exercises?: RtpGymExercise[];
	targets?: RtpFieldTarget[];
}

// The content fields only: a dialog with more fields (days) validates just these for the star.
export const BLOCK_CONTENT_FIELDS: (keyof BlockFormValues)[] = ["description", "exercises", "targets"];

function normalizeExercise(row: RtpGymExercise): RtpGymExercise {
	const exercise: RtpGymExercise = { name: row.name };
	if (row.sets) exercise.sets = row.sets;
	if (row.reps) exercise.reps = row.reps;
	if (row.tempo) exercise.tempo = row.tempo;
	if (row.intensity) exercise.intensity = row.intensity;
	if (row.notes) exercise.notes = row.notes;
	return exercise;
}

function normalizeTarget(row: RtpFieldTarget): RtpFieldTarget {
	const target: RtpFieldTarget = { metricKey: row.metricKey };
	if (row.target) target.target = row.target;
	if (row.note) target.note = row.note;
	return target;
}

// Always returns all three keys, with the empty optional fields of each row dropped.
export function buildBlockContent(values: BlockFormValues): RtpBlockContent {
	return {
		description: values.description ?? "",
		exercises: (values.exercises ?? []).map(normalizeExercise),
		targets: (values.targets ?? []).map(normalizeTarget),
	};
}

export function blockContentToFormValues(content: RtpBlockContent): BlockFormValues {
	return { description: content.description, exercises: content.exercises, targets: content.targets };
}

export const hasBlockContent = (content: RtpBlockContent): boolean =>
	content.description.trim() !== "" || content.exercises.length > 0 || content.targets.length > 0;

interface FieldTargetRowProps {
	name: number;
	restField: { fieldKey?: number };
	onRemove: () => void;
	player?: { id: number; name: string };
}

// One target row (metric + target + remove) with its own metricKey/target watches: picking a
// metric surfaces the player's stats behind an info icon, and an entered target is compared
// against each stat as a percentage.
const FieldTargetRow = ({ name, restField, onRemove, player }: FieldTargetRowProps) => {
	const form = Form.useFormInstance();
	// Field targets are on-pitch work — GPS metrics only.
	const { groups: metricGroups, byKey } = useRtpMetricOptions(["gps"]);
	const metricKey = Form.useWatch(["targets", name, "metricKey"], form);
	const target = Form.useWatch(["targets", name, "target"], form);
	const { rows: statRows } = useRtpMetricStats(player?.id ?? null, metricKey);

	// Target is free text ("3x400m" is valid) — the % comparison only kicks in for plain numbers.
	const targetNum =
		target != null && String(target).trim() !== "" && !Number.isNaN(Number(target)) ? Number(target) : null;
	const infoContent =
		player && metricKey && statRows.length > 0 ? (
			<div className="rtp-target-pop">
				<div className="rtp-target-pop-head">
					{player.name} · {byKey[metricKey]?.label ?? metricKey}
				</div>
				{statRows.map((row) => (
					<div key={row.label}>
						{row.label}: <b>{formatStat(row.value)}</b>
						{targetNum != null && typeof row.value === "number" && row.value !== 0 ? (
							<>
								{" ("}
								{formatStat(targetNum)} is <b>{Math.round((targetNum / row.value) * 100)}%</b>)
							</>
						) : null}
					</div>
				))}
			</div>
		) : null;

	return (
		<div className="rtp-target-row">
			<Form.Item {...restField} name={[name, "metricKey"]} rules={[{ required: true, message: "Required" }]} noStyle>
				<Select
					showSearch
					optionFilterProp="label"
					placeholder="Metric"
					options={metricGroups}
					className="rtp-target-metric"
				/>
			</Form.Item>
			<Form.Item {...restField} name={[name, "target"]} noStyle>
				<Input placeholder="Target" className="rtp-target-value" />
			</Form.Item>
			{/* The slot keeps its width without the icon, so the remove icons line up. */}
			<span className="rtp-target-info-slot">
				{infoContent ? (
					<Popover content={infoContent}>
						<InfoCircleOutlined className="rtp-target-info" aria-label="Player stats for this metric" />
					</Popover>
				) : null}
			</span>
			<Button type="text" size="small" icon={<CloseOutlined />} aria-label="Remove target" onClick={onRemove} />
		</div>
	);
};

type PartKey = "exercises" | "targets";

const PART_ADD_BUTTONS: Record<PartKey, { label: string; dataAttr: string }> = {
	exercises: { label: "Add exercise", dataAttr: "settings.rtp-block.exercise-add" },
	targets: { label: "Add target", dataAttr: "settings.rtp-block.target-add" },
};

const PartHeader = ({ label, count }: { label: string; count: number }) => (
	<div className="rtp-ex-header">
		<span className="rtp-ex-header-label">{label}</span>
		<span className="rtp-ex-header-count">{count} added</span>
	</div>
);

// `contentError` is set by the dialog when a submit had no description, exercise, or target. The
// message under the parts shows only while the form is still without all three.
export const BlockContentFields = ({
	player,
	contentError = false,
	rows = 4,
}: {
	player?: { id: number; name: string };
	contentError?: boolean;
	rows?: number;
}) => {
	const form = Form.useFormInstance();
	const description: string | undefined = Form.useWatch("description", form);
	const exercises: RtpGymExercise[] = Form.useWatch("exercises", form) ?? [];
	const targets: RtpFieldTarget[] = Form.useWatch("targets", form) ?? [];
	const counts: Record<PartKey, number> = { exercises: exercises.length, targets: targets.length };
	const addRow = (key: PartKey) => form.setFieldValue(key, [...(form.getFieldValue(key) ?? []), {}]);
	const addButton = (key: PartKey, block: boolean) => (
		<Button
			key={key}
			className="rtp-ex-add"
			type="dashed"
			block={block}
			icon={<PlusOutlined />}
			data-attr={PART_ADD_BUTTONS[key].dataAttr}
			onClick={() => addRow(key)}
		>
			{PART_ADD_BUTTONS[key].label}
		</Button>
	);
	const emptyParts = (["exercises", "targets"] as const).filter((key) => counts[key] === 0);

	return (
		<>
			<Form.Item name="description">
				<TextArea rows={rows} placeholder="Describe the block" />
			</Form.Item>

			<Form.List name="exercises">
				{(fields, { remove }) =>
					fields.length > 0 ? (
						<div className="rtp-block-part">
							<PartHeader label="Exercises" count={fields.length} />
							<div className="rtp-ex-card">
								<div className="rtp-ex-form-row rtp-ex-form-row--head">
									<div className="rtp-ex-cell-label rtp-ex-form-name-label">Exercise</div>
									{EXERCISE_FIELDS.map(({ field, tableLabel, fullLabel }) => (
										<div key={field} className="rtp-ex-cell-label" title={fullLabel}>
											{tableLabel}
										</div>
									))}
									<div />
								</div>
								{fields.map(({ key, name, ...restField }) => (
									<div key={key} className="rtp-ex-form-row">
										<Form.Item
											{...restField}
											name={[name, "name"]}
											rules={[{ required: true, whitespace: true, message: "Enter an exercise name" }]}
										>
											<TextArea
												size="small"
												className="rtp-ex-form-name"
												placeholder="Exercise name"
												autoSize={{ minRows: 1 }}
												onKeyDown={(e) => {
													if (e.key === "Enter" && !e.shiftKey) e.preventDefault();
												}}
											/>
										</Form.Item>
										{EXERCISE_FIELDS.map(({ field }) => (
											<Form.Item key={field} {...restField} name={[name, field]} noStyle>
												<Input size="small" className="rtp-ex-form-value" placeholder="—" />
											</Form.Item>
										))}
										<Button
											type="text"
											size="small"
											icon={<CloseOutlined />}
											aria-label="Remove exercise"
											data-attr="settings.rtp-block.exercise-remove"
											onClick={() => remove(name)}
										/>
										<Form.Item {...restField} name={[name, "notes"]} noStyle>
											<Input size="small" className="rtp-ex-form-notes" placeholder="Notes…" />
										</Form.Item>
									</div>
								))}
							</div>
							{addButton("exercises", true)}
						</div>
					) : null
				}
			</Form.List>

			<Form.List name="targets">
				{(fields, { remove }) =>
					fields.length > 0 ? (
						<div className="rtp-block-part">
							<PartHeader label="Targets" count={fields.length} />
							{fields.map(({ key, name, ...restField }) => (
								<FieldTargetRow
									key={key}
									name={name}
									restField={restField}
									onRemove={() => remove(name)}
									player={player}
								/>
							))}
							{addButton("targets", true)}
						</div>
					) : null
				}
			</Form.List>

			{emptyParts.length > 0 ? (
				<div className="rtp-block-adds">{emptyParts.map((key) => addButton(key, false))}</div>
			) : null}
			{contentError && !hasBlockContent({ description: description ?? "", exercises, targets }) ? (
				<div className="rtp-block-form-error">{BLOCK_CONTENT_ERROR}</div>
			) : null}
		</>
	);
};
